import type { SkillMastery } from "../adaptive";
import type {
  SessionStoreDocument,
  StoredTypingSession,
} from "../storage";
import { MAX_STORED_SESSIONS } from "../storage";
import { inspectDungeonCharacter } from "../game/dungeon-character";
import type {
  ImportMode,
  ImportPreview,
  ImportResult,
  MasterTypingDataExport,
  ParsedDataExport,
  PortableGameProgression,
  PortablePreferences,
  PortableProfile,
} from "./types";

const VALID_GOALS = new Set(["writing", "gaming", "study", "general"]);
const VALID_WINDOWS = new Set([7, 30, 90]);
const VALID_FONT_SCALES = new Set([1, 1.125, 1.25]);
const VALID_GAME_CHAMPIONS = new Set([
  "wizard",
  "ape",
  "ninja",
  "robot",
  "dragon",
]);
const MAX_CUSTOM_METADATA_ITEMS = 500;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
function safeIso(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? fallback : new Date(timestamp).toISOString();
}

function safeJsonRecord(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function normalizeProfile(value: unknown): PortableProfile {
  const profile = isRecord(value) ? value : {};
  const goal =
    typeof profile.goal === "string" && VALID_GOALS.has(profile.goal)
      ? (profile.goal as PortableProfile["goal"])
      : null;
  const statsWindowDays =
    typeof profile.statsWindowDays === "number" &&
    VALID_WINDOWS.has(profile.statsWindowDays)
      ? (profile.statsWindowDays as PortableProfile["statsWindowDays"])
      : 30;

  return {
    goal,
    hasCompletedOnboarding: Boolean(profile.hasCompletedOnboarding),
    latestRecommendation: safeJsonRecord(profile.latestRecommendation),
    statsWindowDays,
  };
}

function normalizePreferences(value: unknown): PortablePreferences {
  const preferences = isRecord(value) ? value : {};
  const fontScale =
    typeof preferences.fontScale === "number" &&
    VALID_FONT_SCALES.has(preferences.fontScale)
      ? (preferences.fontScale as PortablePreferences["fontScale"])
      : 1;

  return {
    telemetryEnabled: Boolean(preferences.telemetryEnabled),
    highContrast: Boolean(preferences.highContrast),
    reducedMotion: Boolean(preferences.reducedMotion),
    fontScale,
    gamePresentation: safeJsonRecord(preferences.gamePresentation),
  };
}

function normalizeGameProgression(value: unknown): PortableGameProgression {
  const game = isRecord(value) ? value : {};
  const unlockedChampionIds = [
    ...new Set([
      "wizard",
      ...(Array.isArray(game.unlockedChampionIds)
        ? game.unlockedChampionIds.filter(
            (id): id is string =>
              typeof id === "string" && VALID_GAME_CHAMPIONS.has(id),
          )
        : []),
    ]),
  ];
  const selectedChampionId =
    typeof game.selectedChampionId === "string" &&
    unlockedChampionIds.includes(game.selectedChampionId)
      ? game.selectedChampionId
      : "wizard";
  return {
    tutorialCompleted: Boolean(game.tutorialCompleted),
    records: safeJsonRecord(game.records) ?? {},
    dungeonRecords: safeJsonRecord(game.dungeonRecords) ?? {},
    ...(game.dungeonCharacter !== undefined
      ? { dungeonCharacter: structuredClone(game.dungeonCharacter) }
      : {}),
    unlockedChampionIds,
    selectedChampionId,
  };
}

function isStoredSession(value: unknown): value is StoredTypingSession {
  if (!isRecord(value) || !isRecord(value.summary) || !isRecord(value.context)) {
    return false;
  }

  return (
    typeof value.summary.id === "string" &&
    value.summary.id.length > 0 &&
    value.summary.id.length <= 200 &&
    value.summary.schemaVersion === 1 &&
    typeof value.summary.sessionType === "string" &&
    typeof value.summary.startedAt === "string" &&
    typeof value.summary.completedAt === "string" &&
    isRecord(value.summary.validity) &&
    typeof value.context.mode === "string"
  );
}

function isMastery(value: unknown): value is SkillMastery {
  return (
    isRecord(value) &&
    typeof value.skillId === "string" &&
    value.skillId.length > 0 &&
    typeof value.skillType === "string" &&
    typeof value.attempts === "number" &&
    typeof value.masteryScore === "number" &&
    typeof value.confidence === "number" &&
    typeof value.lastUpdatedAt === "string"
  );
}

function createEmptyDocument(nowIso: string): SessionStoreDocument {
  return {
    schemaVersion: 2,
    revision: 0,
    createdAt: nowIso,
    updatedAt: nowIso,
    sessions: [],
    mastery: [],
    processedMasterySessionIds: [],
    migration: null,
  };
}

function parseSessionStore(
  value: unknown,
  nowIso: string,
): {
  document: SessionStoreDocument;
  invalidSessions: number;
  warnings: string[];
} {
  if (!isRecord(value) || value.schemaVersion !== 2) {
    return {
      document: createEmptyDocument(nowIso),
      invalidSessions: 0,
      warnings: ["The export did not contain a supported session store."],
    };
  }

  const rawSessions = Array.isArray(value.sessions) ? value.sessions : [];
  const sessions = rawSessions
    .filter(isStoredSession)
    .slice(-MAX_STORED_SESSIONS)
    .map((session) => structuredClone(session));
  const mastery = (Array.isArray(value.mastery) ? value.mastery : [])
    .filter(isMastery)
    .slice(-5_000)
    .map((item) => structuredClone(item));
  const processedMasterySessionIds = (
    Array.isArray(value.processedMasterySessionIds)
      ? value.processedMasterySessionIds
      : []
  )
    .filter(
      (id): id is string =>
        typeof id === "string" && id.length > 0 && id.length <= 200,
    )
    .slice(-MAX_STORED_SESSIONS);
  const invalidSessions = rawSessions.length - sessions.length;

  return {
    document: {
      schemaVersion: 2,
      revision:
        typeof value.revision === "number" && Number.isFinite(value.revision)
          ? Math.max(0, Math.floor(value.revision))
          : 0,
      createdAt: safeIso(value.createdAt, nowIso),
      updatedAt: safeIso(value.updatedAt, nowIso),
      sessions,
      mastery,
      processedMasterySessionIds,
      migration: null,
    },
    invalidSessions,
    warnings:
      invalidSessions > 0
        ? [
            `${invalidSessions} invalid session${
              invalidSessions === 1 ? " was" : "s were"
            } skipped.`,
          ]
        : [],
  };
}

export function createDataExport(
  input: {
    profile: unknown;
    preferences: unknown;
    sessionStore: SessionStoreDocument;
    legacyStatistics?: unknown;
    gameProgression?: unknown;
    customContentMetadata?: unknown;
  },
  options: {
    appVersion: string;
    now?: Date;
  },
): MasterTypingDataExport {
  const nowIso = (options.now ?? new Date()).toISOString();
  const parsedStore = parseSessionStore(input.sessionStore, nowIso);
  const customContentMetadata = (
    Array.isArray(input.customContentMetadata)
      ? input.customContentMetadata
      : []
  )
    .map(safeJsonRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .slice(0, MAX_CUSTOM_METADATA_ITEMS);

  return {
    format: "mastertyping.user-data",
    schemaVersion: 2,
    appVersion: options.appVersion,
    exportedAt: nowIso,
    data: {
      profile: normalizeProfile(input.profile),
      preferences: normalizePreferences(input.preferences),
      sessionStore: parsedStore.document,
      legacyStatistics: safeJsonRecord(input.legacyStatistics),
      gameProgression: normalizeGameProgression(input.gameProgression),
      customContentMetadata,
    },
  };
}

export function parseDataExport(
  value: unknown,
  existingDocument?: SessionStoreDocument,
  now: Date = new Date(),
): ParsedDataExport {
  const emptyPreview: ImportPreview = {
    valid: false,
    errors: [],
    warnings: [],
    sessions: 0,
    masterySkills: 0,
    duplicateSessions: 0,
    invalidSessions: 0,
    profileIncluded: false,
    preferencesIncluded: false,
    gameRecords: 0,
    exportedAt: null,
  };

  if (!isRecord(value)) {
    emptyPreview.errors.push("The selected file is not a JSON object.");
    return { value: null, preview: emptyPreview };
  }
  if (
    value.format !== "mastertyping.user-data" ||
    value.schemaVersion !== 2 ||
    !isRecord(value.data)
  ) {
    emptyPreview.errors.push(
      "This is not a supported MasterTyping version 2 export.",
    );
    return { value: null, preview: emptyPreview };
  }

  const nowIso = now.toISOString();
  const parsedStore = parseSessionStore(value.data.sessionStore, nowIso);
  const profile = normalizeProfile(value.data.profile);
  const preferences = normalizePreferences(value.data.preferences);
  const gameProgression = normalizeGameProgression(value.data.gameProgression);
  const existingIds = new Set(
    (existingDocument?.sessions ?? []).map((session) => session.summary.id),
  );
  const duplicateSessions = parsedStore.document.sessions.filter((session) =>
    existingIds.has(session.summary.id),
  ).length;
  const exportedAt = safeIso(value.exportedAt, "");
  const exportValue: MasterTypingDataExport = {
    format: "mastertyping.user-data",
    schemaVersion: 2,
    appVersion:
      typeof value.appVersion === "string" ? value.appVersion : "unknown",
    exportedAt: exportedAt || nowIso,
    data: {
      profile,
      preferences,
      sessionStore: parsedStore.document,
      legacyStatistics: safeJsonRecord(value.data.legacyStatistics),
      gameProgression,
      customContentMetadata: (
        Array.isArray(value.data.customContentMetadata)
          ? value.data.customContentMetadata
          : []
      )
        .map(safeJsonRecord)
        .filter((item): item is Record<string, unknown> => item !== null)
        .slice(0, MAX_CUSTOM_METADATA_ITEMS),
    },
  };

  return {
    value: exportValue,
    preview: {
      valid: true,
      errors: [],
      warnings: [
        ...parsedStore.warnings,
        ...(["future", "corrupt"].includes(inspectDungeonCharacter(gameProgression.dungeonCharacter).status)
          ? ["The dungeon character needs a compatible version or recovery. Its original data is retained in this backup."]
          : []),
      ],
      sessions: parsedStore.document.sessions.length,
      masterySkills: parsedStore.document.mastery.length,
      duplicateSessions,
      invalidSessions: parsedStore.invalidSessions,
      profileIncluded: true,
      preferencesIncluded: true,
      gameRecords: Object.keys(gameProgression.records).length,
      exportedAt: exportValue.exportedAt,
    },
  };
}

function newestMastery(
  existing: readonly SkillMastery[],
  incoming: readonly SkillMastery[],
): SkillMastery[] {
  const bySkill = new Map<string, SkillMastery>();
  for (const item of [...existing, ...incoming]) {
    const previous = bySkill.get(item.skillId);
    if (
      !previous ||
      Date.parse(item.lastUpdatedAt) >= Date.parse(previous.lastUpdatedAt)
    ) {
      bySkill.set(item.skillId, structuredClone(item));
    }
  }
  return [...bySkill.values()];
}

export function applyDataImport(
  existing: SessionStoreDocument,
  incoming: MasterTypingDataExport,
  mode: ImportMode,
  now: Date = new Date(),
): ImportResult {
  const incomingDocument = incoming.data.sessionStore;
  const existingIds = new Set(
    existing.sessions.map((session) => session.summary.id),
  );
  const duplicateSessions = incomingDocument.sessions.filter((session) =>
    existingIds.has(session.summary.id),
  ).length;
  const nowIso = now.toISOString();

  if (mode === "replace") {
    return {
      document: {
        ...structuredClone(incomingDocument),
        revision: Math.max(existing.revision, incomingDocument.revision) + 1,
        updatedAt: nowIso,
      },
      importedSessions: incomingDocument.sessions.length,
      duplicateSessions: 0,
      invalidSessions: 0,
      importedMasterySkills: incomingDocument.mastery.length,
    };
  }

  const addedSessions = incomingDocument.sessions.filter(
    (session) => !existingIds.has(session.summary.id),
  );
  const mergedSessions = [...existing.sessions, ...addedSessions].slice(
    -MAX_STORED_SESSIONS,
  );

  return {
    document: {
      ...structuredClone(existing),
      revision: existing.revision + 1,
      updatedAt: nowIso,
      sessions: mergedSessions,
      mastery: newestMastery(existing.mastery, incomingDocument.mastery),
      processedMasterySessionIds: [
        ...new Set([
          ...existing.processedMasterySessionIds,
          ...incomingDocument.processedMasterySessionIds,
        ]),
      ].slice(-MAX_STORED_SESSIONS),
    },
    importedSessions: addedSessions.length,
    duplicateSessions,
    invalidSessions: 0,
    importedMasterySkills: incomingDocument.mastery.length,
  };
}
